import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckIcon, ClipboardIcon } from "@heroicons/react/24/outline";

import { TableReportDateWithFormatAndClass } from "../shared-components/TableReportDateWithFormat";
import Badge from "../shared-components/Badge";

import { usePermissionValidator } from "../api/UserPermissionValidators";
import { ThreeSixtyFinishedRow } from "../api/ThreeSixty";
import ReportsCircleTooltip from "../reports/helper-components/ReportsCircleTooltip";
import {
  ReportEntryStyle,
  ReportRowItem,
} from "../reports/helper-components/TableModels";
import { FlagsCountPayload } from "../api/Reports";
import ThreeSixtyActionFinishedActionMenu from "./ThreeSixtyActionFinishedActionMenu";
import PDFDownloadWrapper from "../reports/PDFDownloadWrapper";
import ArrowDownTrayIcon from "../shared-components/icons/ArrowDownTrayIcon";
import {
  getRerunTooltipId,
  isRerunRow,
  ThreeSixtyRerunIcon,
  ThreeSixtyRerunTooltip,
} from "./ThreeSixtyRerunIcon";

type ThreeSixtyFinishedRowProps = {
  row: ThreeSixtyFinishedRow;
  cancel: string[];
  toggleCancel: (value: string) => number;
};

function transforedRow(
  row: ThreeSixtyFinishedRow
): ReportRowItem<{ flags_count_payload?: FlagsCountPayload }> {
  const flags_count_payload: FlagsCountPayload = {
    community_count: row.insights?.community || 0,
    adverse_flag_count: row.insights?.adverse || 0,
    articles_count: row.insights?.articles || 0,
    keywords: row.insights?.keywords || 0,
    topics: row.insights?.topics || 0,
    news: row.insights?.news || 0,
    legal: row.insights?.legal || 0,
    diligence: row.insights?.diligence || 0,
  };
  return {
    report_uuid: row.request_uuid,
    data: [{ text: "", style: ReportEntryStyle.TEXT }],
    isLocked: false,
    report: { flags_count_payload: flags_count_payload },
  };
}

export const ThreeSixtyFinishedRowRenderer = (
  props: ThreeSixtyFinishedRowProps
) => {
  const [copied, setCopied] = useState<boolean>(false);
  const location = useLocation();
  const row: ThreeSixtyFinishedRow = props.row;
  const perms = usePermissionValidator();
  const isRerun = isRerunRow(row);
  const rerunTooltipId = getRerunTooltipId(row.request_uuid);

  function copyToClipboard(value: string) {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="flex gap-2 px-2 py-5 border-b text-gray-700">
      <div className="flex-1 flex items-center gap-2 overflow-auto pr-4">
        <div className="flex-1">
          <div className="flex items-start">
            {perms.canViewWIP360Reports ||
            perms.canEdit360Reports ||
            perms.canManageReports ? (
              <Link
                className="underline font-semibold"
                to={`/360/${row.request_uuid}/summary`}
                state={{
                  originUrl: `${location.pathname}${location.search}`,
                }}
              >
                <span>{row.name}</span>
              </Link>
            ) : (
              <span className="font-semibold">{row.name}</span>
            )}
            {isRerun && <ThreeSixtyRerunIcon tooltipId={rerunTooltipId} />}
          </div>
          <div
            className="text-xs text-clip whitespace-nowrap mt-1 flex items-center text-gray-700  rounded-md py-1 hover:bg-gray-100 hover:cursor-pointer"
            onClick={() => copyToClipboard(row.request_uuid)}
          >
            {copied ? (
              <CheckIcon className="w-4 inline-block stroke-green-500" />
            ) : (
              <ClipboardIcon className="w-4 inline-block" />
            )}
            <span className="pl-1">{row.request_uuid.slice(0, 8)}</span>
          </div>
        </div>
      </div>
      {isRerun && <ThreeSixtyRerunTooltip tooltipId={rerunTooltipId} />}
      <div className="flex-1 flex items-center w-20">
        <ReportsCircleTooltip
          reportRow={transforedRow(row)}
          flagKey={row.request_uuid}
        />
      </div>
      <div className="flex-1 flex items-center">{row.company_name}</div>
      <div className="flex-1">
        <div className="text-md">{row.blueprint_name}</div>
        <div className="text-md mt-2">{row.behavior_kit_name || "General"}</div>
      </div>
      <div className="flex-1 flex items-center">
        <div className="flex items-center gap-1">
          {!row.finished_at ? (
            <Badge color="gray"> </Badge>
          ) : row.finished_at <= row.due_date ? (
            <Badge color="green">Yes</Badge>
          ) : (
            <Badge color="red">No</Badge>
          )}
          {row.finished_at ? (
            <PDFDownloadWrapper report_uuid={row.request_uuid}>
              <div className="inline-flex shrink-0 cursor-pointer text-gray-700 hover:text-gray-900">
                <ArrowDownTrayIcon />
              </div>
            </PDFDownloadWrapper>
          ) : null}
        </div>
      </div>
      <div className="flex-1 flex gap-2 whitespace-pre text-md items-center">
        {!row.finished_at ? (
          <div>N/A</div>
        ) : (
          <TableReportDateWithFormatAndClass date={row.finished_at ?? ""} />
        )}
      </div>
      <div className="flex-1 flex items-center">{row.custom_id}</div>
      <div className="flex-1 flex items-center p-2">
        <ThreeSixtyActionFinishedActionMenu reportUUID={row.request_uuid} />
      </div>
    </div>
  );
};
